import { Award, Camera, Globe, type LucideIcon } from "lucide-react";
import { LANDING_IMAGES } from "../../../../constants/landing-images";

export type ProductLandingSlug = "website" | "job-photos" | "credentials";

export type ProductLandingDefinition = {
  slug: ProductLandingSlug;
  href: string;
  navLabel: string;
  name: string;
  kicker: string;
  kickerIcon: LucideIcon;
  heroH1: string;
  heroSubhead: string;
  standAlone: string;
  cardDescription: string;
  cardBullets: readonly string[];
  image: string;
  imageAlt: string;
  benefits: ReadonlyArray<{ title: string; body: string }>;
  mobilePitch: string;
};

export const PRODUCT_LANDING_DEFINITIONS: Record<
  ProductLandingSlug,
  ProductLandingDefinition
> = {
  website: {
    slug: "website",
    href: "/website",
    navLabel: "Website",
    name: "Website",
    kicker: "Get found",
    kickerIcon: Globe,
    heroH1: "Get found. Take quotes.",
    heroSubhead:
      "Your own contractor site. Neighbors request a quote while you are on a job.",
    standAlone:
      "Use the website alone. Job photos and licenses are there when you want them.",
    cardDescription:
      "Your own site. Neighbors request a quote while you are on a job.",
    cardBullets: [
      "Live on a toolkit.build address",
      "Quote inbox on your phone",
      "A page for each service and town",
    ],
    image: LANDING_IMAGES.productWebsite,
    imageAlt: "Finished home exterior for a contractor website portfolio",
    benefits: [
      {
        title: "Live in about a minute",
        body: "Chat about your trade. Walk away with a site on a toolkit.build address.",
      },
      {
        title: "Local pages",
        body: "A page for each service and town you work, so nearby people can find you.",
      },
      {
        title: "Quotes on your phone",
        body: "They request a quote from the site. You get it even if you are on a job.",
      },
      {
        title: "Confirm the visit",
        body: "Mark quotes read and confirm visit times in the Toolkit app.",
      },
    ],
    mobilePitch:
      "Review quotes and confirm visits from the Toolkit app. Built for the phone in your truck.",
  },
  "job-photos": {
    slug: "job-photos",
    href: "/job-photos",
    navLabel: "Job photos",
    name: "Job-site photos",
    kicker: "Proof from the field",
    kickerIcon: Camera,
    heroH1: "Photos on the right job.",
    heroSubhead: "Add a site. Shoot in the Toolkit app. GPS files every shot.",
    standAlone:
      "Skip the public site. Track jobs, photos, and punch lists from your phone.",
    cardDescription:
      "Shoot in the Toolkit app or upload from the website. GPS puts each shot on the right job.",
    cardBullets: [
      "Phone or website",
      "GPS matches photos to the job",
      "Tags for before/after and punch lists",
    ],
    image: LANDING_IMAGES.productPhotos,
    imageAlt: "Construction crew documenting work on a job site",
    benefits: [
      {
        title: "Sites, not folders",
        body: "Pick the street address. Set the map radius. Every shot lands on that job.",
      },
      {
        title: "Shoot on site",
        body: "Capture in the Toolkit app or upload from the website. GPS does the filing.",
      },
      {
        title: "Find it later",
        body: "Tags for before/after, punch lists, and the work you need to prove.",
      },
      {
        title: "Crew or solo",
        body: "Invite people to a site. They accept in the app. You can run it alone.",
      },
    ],
    mobilePitch:
      "This is the phone product. Shoot on the job. Swipe back to Home.",
  },
  credentials: {
    slug: "credentials",
    href: "/credentials",
    navLabel: "Credentials",
    name: "Credentials",
    kicker: "Don't lapse",
    kickerIcon: Award,
    heroH1: "Don't let a license lapse.",
    heroSubhead:
      "Photograph a card. We read the dates. You get a reminder before it expires.",
    standAlone: "Just the vault. No website required to stay current.",
    cardDescription:
      "Photograph a license. We read the dates. You get a reminder before it expires.",
    cardBullets: [
      "Photo on the website or in the app",
      "Email and push reminders",
      "Share a link, or export PDF and CSV",
    ],
    image: LANDING_IMAGES.productCredentials,
    imageAlt: "Trade license and certification documents on a desk",
    benefits: [
      {
        title: "Photograph the card",
        body: "Take it on the website or in the app. We read the type, issued date, and expiry.",
      },
      {
        title: "Reminders before it expires",
        body: "Email and push before a license or cert lapses. Add or drop reminders per card.",
      },
      {
        title: "Share a link",
        body: "One URL. Turn it on or off. It does not expire or rotate.",
      },
      {
        title: "Crew or just you",
        body: "Owners and admins see every card in the company. Members see their own. Export PDF or CSV.",
      },
    ],
    mobilePitch:
      "Photograph a card in the truck. Get the reminder on your phone.",
  },
};

export const PRODUCT_LANDING_ORDER: readonly ProductLandingSlug[] = [
  "website",
  "job-photos",
  "credentials",
];

export const PRODUCT_LANDING_NAV_ENTRIES: ReadonlyArray<{
  slug: ProductLandingSlug;
  href: string;
  navLabel: string;
}> = PRODUCT_LANDING_ORDER.map((slug) => {
  const definition = PRODUCT_LANDING_DEFINITIONS[slug];
  return {
    slug: definition.slug,
    href: definition.href,
    navLabel: definition.navLabel,
  };
});
